import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Users, Calendar, Mail, Shield, Clock, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import favicon from '/public/favicon.svg';
import demoThumbnail from '/public/lovable-uploads/munger-demo-thumbnail.jpg';

interface ExhaustedHeroSectionProps {
  onStartClick?: () => void;
}

const ExhaustedHeroSection: React.FC<ExhaustedHeroSectionProps> = ({ onStartClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [currentTime, setCurrentTime] = useState("2:17 AM");
  const [founderCount, setFounderCount] = useState(2147);
  const navigate = useNavigate();

  // Increment founder count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setFounderCount(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Handler for sign in
  const handleSignIn = () => {
    navigate('/login');
  };

  // Sticky navigation with dual CTAs
  const StickyNav = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src={favicon} 
            alt="Munger.ai" 
            className="h-8 w-8 mr-2"
          />
          <span className="font-bold text-lg sm:text-xl text-gray-900">Munger.ai</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleSignIn}
            className="text-gray-600 hover:text-gray-900 text-xs sm:text-sm px-3 sm:px-4"
          >
            Sign In
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/book-demo')}
            className="border-gray-300 hover:border-gray-400 text-xs sm:text-sm px-3 sm:px-4"
          >
            <span className="hidden sm:inline">Book </span>Demo
          </Button>
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4" 
            onClick={onStartClick}
          >
            Start Free<span className="hidden sm:inline"> →</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Sticky Navigation */}
      <StickyNav />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="text-center">
          {/* Late night indicator */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8"
          >
            <Badge className="bg-red-900/30 text-red-300 border-red-800 text-xs sm:text-sm">
              <Clock className="w-3 h-3 mr-1" />
              {currentTime}
            </Badge>
            <Badge className="bg-amber-900/30 text-amber-300 border-amber-800 text-xs sm:text-sm">
              <Coffee className="w-3 h-3 mr-1" />
              Cup #6
            </Badge>
            <Badge className="bg-green-900/30 text-green-300 border-green-800 text-xs sm:text-sm">
              <Users className="w-3 h-3 mr-1" />
              {founderCount.toLocaleString()} founders stopped writing emails today
            </Badge>
          </motion.div>

          {/* Main content wrapper */}
          <div className="relative">
            {/* Clear value proposition */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight px-4"
            >
              Still Writing Cold Emails at 2am?
            </motion.h1>

            {/* Supporting copy - concrete benefits */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto px-4"
            >
              While you crafted email #47, your competitor booked 12 meetings with AI
            </motion.p>

            {/* Hero video with play button overlay */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="relative max-w-4xl mx-auto mb-12"
            >
              {!showVideo ? (
                <div 
                  className="relative cursor-pointer group"
                  onClick={() => setShowVideo(true)}
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Video thumbnail */}
                    <img 
                      src={demoThumbnail} 
                      alt="Munger AI Demo - Automated Pipeline"
                      className="w-full h-full object-cover opacity-70"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                        <Play className="w-12 h-12 text-blue-600 ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                      Watch how AI writes emails while you sleep
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video rounded-2xl shadow-2xl overflow-hidden">
                  <iframe 
                    src="https://www.loom.com/embed/97abf599533e4e81bf82399f0b5b6de3?sid=dc70419a-250e-4407-8357-ffe11dcb59b0&autoplay=1"
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
              )}
            </motion.div>

            {/* Dual CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={onStartClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Start Sleeping Again →
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/book-demo')}
                  className="border-2 border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white px-8 py-6 text-lg rounded-lg"
                >
                  Book Demo
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-xs sm:text-sm text-gray-400 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4"
            >
              <span>✅ 30-day money-back guarantee</span>
              <span className="hidden sm:inline">✅ Setup in under 10 minutes</span>
              <span>✅ Cancel anytime</span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhaustedHeroSection; 